import React, { useCallback, useEffect, useRef, useState } from "react";

const DIAL_KEYS = [
  { num: '1', letters: '' },
  { num: '2', letters: 'ABC' },
  { num: '3', letters: 'DEF' },
  { num: '4', letters: 'GHI' },
  { num: '5', letters: 'JKL' },
  { num: '6', letters: 'MNO' },
  { num: '7', letters: 'PQRS'},
  { num: '8', letters: 'TUV' },
  { num: '9', letters: 'WXYZ'},
  { num: '*', letters: '' },
  { num: '0', letters: '+' },
  { num: '#', letters: '' },
];
  
const VALID_KEYS = new Set(['0','1','2','3','4','5','6','7','8','9','*','#']);
  
export default function DialPad({ onDigit }: { onDigit: (digit: string) => void }) {
  const [value, setValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback((num: string) => {
    setValue(v => v + num);
    onDigit(num);
  }, [onDigit]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (VALID_KEYS.has(e.key)) {
      setValue(v => v + e.key);
      onDigit(e.key);
      e.preventDefault();
    }
  }, [onDigit]);

  const handlePaste = useCallback((e: ClipboardEvent) => {
    const pasted = e.clipboardData?.getData('text') || '';
    const chars = Array.from(pasted).filter(char => VALID_KEYS.has(char));
    if (chars.length) {
      const sequence = chars.join('');
      setValue(v => v + sequence);
      onDigit(sequence); // send all digits at once
    }
    e.preventDefault();
  }, [onDigit]);

  // Key press and paste handling
  useEffect(() => {
    const node = containerRef.current;
    if (node) {
      node.focus();
      node.addEventListener('keydown', handleKeyDown);
      node.addEventListener('paste', handlePaste);
    }
    return () => {
      if (node) {
        node.removeEventListener('keydown', handleKeyDown);
        node.removeEventListener('paste', handlePaste);
      }
    };
  }, [handleKeyDown, handlePaste]);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className="outline-none"
      style={{ userSelect: 'none' }}
    >
      <div className="text-center text-3xl font-mono min-h-[2.5rem]">{value}</div>
      <div className="max-w-xs grid grid-cols-3 gap-2 mx-auto p-4">
        {DIAL_KEYS.map(({ num, letters }) => (
          <button
            key={num}
            className="aspect-square flex flex-col border rounded-xl hover:bg-[#e8e8e8] transition-all cursor-pointer"
            onClick={() => handleClick(num)}
            tabIndex={-1}
            type="button"
          >
            <span className="text-[1.75rem] leading-none mt-4">{num}</span>
            {letters && (
              <span className="mt-1 text-sm text-gray-500">
                {letters}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
